/**
 * HandoffBriefing — DIGEMP-HUMAN-HANDOFF-FRONTEND-058.
 *
 * Read-only briefing for a piece of work a Digital Employee stopped and handed to a human:
 * what the employee found, what it needs, the context it worked with, and what evidence exists.
 * The point is that a technician can pick the work up WITHOUT rereading the conversation or the
 * raw run log — so every section is built ONLY from structured AgentRun fields (analysis summary /
 * likely_issue / recommended_next_step / confidence / missing_information / evidence_sources,
 * result_summary, context.*). Never analysis.reasoning, never context.recent_activity, never raw
 * tool payloads. Missing data is stated as missing instead of being rendered as a success.
 *
 * The same helpers drive the handoff queue page, so a row and its briefing always agree.
 */
import { Link } from "react-router-dom";
import EmployeeAvatar from "./EmployeeAvatar";
import { EVIDENCE_SOURCE_LABEL } from "./evidenceSourceVocabulary";
import { actionTypeLabel } from "./actionTypeVocabulary";
import { runStatusLabel } from "./runStatusVocabulary";
import { taskTypeLabel } from "./taskTypeVocabulary";

// The run statuses that mean "the employee is waiting for a human". Mirrors the wording the
// conversation surface already uses (conversation_service.stage_human_text) so both surfaces
// describe the same wait the same way. Everything else is either active work or terminal.
export const WAITING_STAGES = {
  awaiting_technician_review: {
    kind: "review",
    label: "ממתין לסקירת טכנאי",
    required: "לסקור את הממצאים ולהחליט על הצעד הבא",
    voice: "סיימתי את הבדיקה. ממתין/ה לסקירה שלך.",
  },
  awaiting_approval: {
    kind: "approval",
    label: "ממתין לאישור",
    required: "לאשר או לדחות את הפעולה המוצעת לפני שממשיכים",
    voice: "סיימתי את הבדיקה. ממתין/ה לאישור שלך לפני שממשיכים.",
  },
  waiting_for_customer_connection: {
    kind: "customer",
    label: "ממתין לחיבור לקוח",
    required: "לתאם את החיבור מול הלקוח כדי לאפשר המשך",
    voice: "ממתין/ה ללקוח (חיבור) כדי להמשיך.",
  },
  waiting_for_customer_answer: {
    kind: "customer",
    label: "ממתין לתשובת לקוח",
    required: "להשיג מהלקוח את התשובה או המידע החסר",
    voice: "ממתין/ה ללקוח (תשובה) כדי להמשיך.",
  },
  paused: {
    kind: "paused",
    label: "מושהה",
    required: "להחליט אם להמשיך, לשנות או לעצור את המשימה",
    voice: "המשימה הושהתה.",
  },
};

export const HANDOFF_GROUPS = [
  { kind: "review", title: "המתנה לסקירה", statuses: ["awaiting_technician_review"] },
  { kind: "approval", title: "המתנה לאישור", statuses: ["awaiting_approval"] },
  { kind: "customer", title: "המתנה ללקוח", statuses: ["waiting_for_customer_connection", "waiting_for_customer_answer"] },
  { kind: "paused", title: "מושהות", statuses: ["paused"] },
];

export const HANDOFF_STATUSES = HANDOFF_GROUPS.flatMap((g) => g.statuses);

const STAGE_STYLE = {
  review: "bg-amber-100 text-amber-800 border-amber-200",
  approval: "bg-amber-100 text-amber-800 border-amber-200",
  customer: "bg-blue-100 text-blue-700 border-blue-200",
  paused: "bg-gray-100 text-gray-600 border-gray-200",
};

// The scale the producer writes (`AnalysisOutput.risk_level`: green/amber/red, the same three
// words every other surface renders), not a hand-written one. The previous map was keyed
// low/medium/high, which no producer emits, so every lookup missed and the Hebrew briefing
// printed the raw token instead of a grade.
const RISK_LABEL = { green: "נמוך", amber: "בינוני", red: "גבוה" };

export function handoffStage(status) {
  const stage = WAITING_STAGES[status];
  if (!stage) return null;
  return { status, ...stage };
}

/** Hebrew waiting duration; null when the timestamp is missing or unparsable — never "0 דקות". */
export function waitingHebrew(iso, now = Date.now()) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const mins = Math.max(0, Math.floor((now - t) / 60000));
  if (mins < 1) return "זה עתה";
  if (mins < 60) return `${mins} דק׳`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} שע׳`;
  return `${Math.floor(hours / 24)} ימים`;
}

/**
 * One line the employee itself stands behind: result first, then its own analysis summary.
 *
 * `degraded` rides the sentence it qualifies, so it is returned only when the line the caller will
 * actually render came out of the analysis. A line taken from `result_summary` is the run's own
 * executed result, which a fallback analysis does not make untrue, so it carries no disclosure —
 * the scoping rule FULL-0036 records for the ticket screen's two analysis surfaces.
 */
export function handoffSummary(run) {
  const a = run?.analysis || {};
  const picked = [
    { text: run?.result_summary, fromAnalysis: false },
    { text: a.summary, fromAnalysis: true },
    { text: a.likely_issue, fromAnalysis: true },
  ].find((s) => typeof s.text === "string" && s.text.trim());
  if (!picked) return { text: null, degraded: null };
  return {
    text: picked.text.trim(),
    degraded: picked.fromAnalysis ? (a.degraded || null) : null,
  };
}

function bounded(value, max) {
  if (typeof value !== "string" || !value.trim()) return null;
  const text = value.trim();
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

function stringList(value) {
  return Array.isArray(value) ? value.filter((v) => typeof v === "string" && v.trim()).map((v) => v.trim()) : [];
}

function executionLine(execution) {
  if (!execution || typeof execution !== "object") return null;
  const message = bounded(execution.message, 300);
  const type = actionTypeLabel(execution.action_type);
  if (message) return { ok: Boolean(execution.success && execution.executed), text: message, type };
  if (type) return { ok: Boolean(execution.success), text: null, type };
  return null;
}

/**
 * Package a run into the briefing shape. Unknown fields stay null and the component renders the
 * request to re-check the source run rather than pretending the section is empty by design.
 */
export function buildBriefing(run) {
  const a = run?.analysis || {};
  const ctx = run?.context || {};
  const env = ctx.customer_environment || {};
  const stage = handoffStage(run?.status);

  const confidence =
    typeof a.confidence === "number" && Number.isFinite(a.confidence) ? a.confidence : null;

  const openQuestions = stringList(a.missing_information).map((text) => ({ text, source: "missing_information" }));

  const environment = [
    env.ms365_domain ? { label: "דומיין M365", value: env.ms365_domain } : null,
    env.endpoint_count != null ? { label: "תחנות קצה", value: String(env.endpoint_count) } : null,
    env.av_vendor ? { label: "אנטי-וירוס", value: env.av_vendor } : null,
    env.backup_vendor ? { label: "גיבוי", value: env.backup_vendor } : null,
  ].filter(Boolean);

  const kbArticles = (Array.isArray(ctx.kb_articles_used) ? ctx.kb_articles_used : [])
    .map((art) => (art && typeof art === "object" ? { id: art.id ?? null, title: bounded(art.title, 160) } : null))
    .filter(Boolean)
    .slice(0, 5);

  const similarTickets = (Array.isArray(ctx.similar_ticket_references) ? ctx.similar_ticket_references : [])
    .map((ref) => (ref && typeof ref === "object"
      ? { id: ref.ticket_id ?? ref.id ?? null, title: bounded(ref.title, 160), resolution: bounded(ref.resolution, 200) }
      : null))
    .filter(Boolean)
    .slice(0, 3);

  const conversationId = typeof ctx.conversation_id === "string" && ctx.conversation_id.trim()
    ? ctx.conversation_id.trim()
    : null;

  const hasFindings = Boolean(
    (typeof a.summary === "string" && a.summary.trim())
    || (typeof a.likely_issue === "string" && a.likely_issue.trim())
    || (typeof run?.result_summary === "string" && run.result_summary.trim())
  );

  return {
    stage,
    hasFindings,
    findings: {
      summary: bounded(a.summary, 700),
      likelyIssue: bounded(a.likely_issue, 500),
      recommendation: bounded(a.recommended_next_step, 500),
      resultSummary: bounded(run?.result_summary, 700),
      confidence,
      riskLevel: typeof a.risk_level === "string" ? a.risk_level : null,
      taskType: typeof a.task_type === "string" ? a.task_type : null,
      degraded: a.degraded || null,
      proposedAction: a.proposed_action && typeof a.proposed_action === "object"
        ? {
            description: bounded(a.proposed_action.description, 400),
            actionType: a.proposed_action.action_type ?? null,
          }
        : null,
      customerReplyDraft: bounded(a.customer_reply_draft, 500),
    },
    openQuestions,
    context: {
      inputSummary: bounded(run?.input_summary, 500),
      ticketDescription: bounded(ctx.ticket_description, 700),
      technician: bounded(run?.ticket_technician_name, 120) || bounded(ctx.technician, 120),
      deviceIdentifier: bounded(ctx.device_identifier, 160),
      environment,
      kbArticles,
      similarTickets,
    },
    evidence: {
      sources: stringList(a.evidence_sources),
      execution: executionLine(ctx.execution_result),
      startedAt: run?.started_at || null,
      finishedAt: run?.finished_at || null,
      updatedAt: run?.updated_at || null,
      conversationId,
    },
  };
}

function Section({ title, children, testId }) {
  return (
    <section data-testid={testId} className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
      <h2 className="font-semibold text-gray-700 text-sm border-b pb-2">{title}</h2>
      {children}
    </section>
  );
}

function KV({ label, value }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-gray-500 shrink-0 w-32">{label}:</span>
      <span className="text-gray-800 break-words">{value}</span>
    </div>
  );
}

function Unknown({ children, ...rest }) {
  return <p className="text-xs text-gray-400 italic" {...rest}>{children}</p>;
}

export default function HandoffBriefing({ run, conversation = null, conversationError = false }) {
  if (!run) return null;
  const b = buildBriefing(run);
  const f = b.findings;
  const confColor = f.confidence == null ? "text-gray-400"
    : f.confidence >= 75 ? "text-green-600"
    : f.confidence >= 45 ? "text-yellow-600" : "text-red-500";
  const employeeName = run.agent_name || "עובד דיגיטלי";

  const links = [
    { key: "run", label: "ריצת AI", to: `/agent-runs/${run.id}`, available: true },
    run.ticket_id
      ? { key: "ticket", label: run.ticket_number != null ? `קריאה #${run.ticket_number}` : "קריאה", to: `/tickets/${run.ticket_id}`, available: true }
      : { key: "ticket", label: "קריאה", to: null, available: false },
    b.evidence.conversationId
      ? { key: "conversation", label: "שיחת AI", to: `/conversations/${b.evidence.conversationId}`, available: true }
      : { key: "conversation", label: "שיחת AI", to: null, available: false },
  ];

  return (
    <div className="space-y-4" data-testid="handoff-briefing" dir="rtl">
      <header className="bg-white rounded-xl border shadow-sm p-5 space-y-3">
        <div className="flex items-center gap-2">
          <EmployeeAvatar name={employeeName} size="sm" />
          <div>
            <p className="font-semibold text-gray-800 text-sm">{employeeName}</p>
            <p className="text-xs text-gray-400">תדריך מסירה לאדם</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {b.stage ? (
            <span className={`px-2 py-0.5 rounded-full border font-medium ${STAGE_STYLE[b.stage.kind]}`}>
              {b.stage.label}
            </span>
          ) : (
            <span className="px-2 py-0.5 rounded-full border bg-gray-100 text-gray-600 border-gray-200">
              {runStatusLabel(run.status)}
            </span>
          )}
          <span className="text-gray-500">
            {run.ticket_number != null ? `קריאה #${run.ticket_number}` : "קריאה"}
            {run.ticket_title ? ` — ${run.ticket_title}` : ""}
          </span>
          {run.ticket_company_name && <span className="text-gray-400">· {run.ticket_company_name}</span>}
          <span className="text-gray-400">
            {waitingHebrew(b.evidence.updatedAt)
              ? `ממתין לאדם ${waitingHebrew(b.evidence.updatedAt)}`
              : "זמן ההמתנה לא ידוע"}
          </span>
        </div>
      </header>

      {b.stage && (
        <section data-testid="required-action" className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-1">
          <p className="text-xs font-semibold text-amber-800">מה נדרש ממך</p>
          <p className="text-sm text-amber-900">{b.stage.required}</p>
          <p className="text-xs text-amber-700">העובד: „{b.stage.voice}”</p>
        </section>
      )}

      <Section title="ממצאים" testId="briefing-findings">
        {!b.hasFindings && !f.proposedAction && (
          <Unknown data-testid="briefing-no-findings">
            העובד לא סיפק ניתוח או סיכום תוצאה לריצה הזו — יש לפתוח את ריצת ה-AI כדי לבדוק את מצבה.
          </Unknown>
        )}
        {f.degraded && (
          <div role="alert" data-testid="briefing-degraded" className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
            {f.degraded === "mock"
              ? "זהו ניתוח דמה: ספק ה-AI אינו מוגדר, ולכן התוכן אינו אבחון אמיתי."
              : "תשובת המודל לא נקראה כראוי, ולכן זהו ניתוח חלופי בביטחון נמוך ולא אבחון אמיתי."}
          </div>
        )}
        {f.summary && <p className="text-sm text-gray-800 leading-relaxed">{f.summary}</p>}
        {f.likelyIssue && (
          <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
            <p className="text-xs font-medium text-blue-700 mb-0.5">בעיה סבירה:</p>
            <p className="text-sm text-blue-900">{f.likelyIssue}</p>
          </div>
        )}
        {f.recommendation && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">הצעד הבא שהעובד ממליץ:</p>
            <p className="text-sm text-gray-700">{f.recommendation}</p>
          </div>
        )}
        {f.resultSummary && f.resultSummary !== f.summary && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">סיכום התוצאה:</p>
            <p className="text-sm text-gray-700">{f.resultSummary}</p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 text-xs">
          {f.confidence != null && (
            <span className={`font-semibold ${confColor}`}>ביטחון {f.confidence}%</span>
          )}
          {f.riskLevel && <span className="text-gray-500">סיכון: {RISK_LABEL[f.riskLevel] || f.riskLevel}</span>}
          {f.taskType && <span className="text-gray-500">סוג משימה: {taskTypeLabel(f.taskType)}</span>}
        </div>
        {f.proposedAction?.description && (
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-blue-700 mb-1">פעולה מוצעת שעליה מתבקשת החלטה:</p>
            <p className="text-sm text-gray-800">
              {f.proposedAction.description}
              {f.proposedAction.actionType && (
                <span className="text-gray-400"> ({actionTypeLabel(f.proposedAction.actionType)})</span>
              )}
            </p>
          </div>
        )}
        {f.customerReplyDraft && (
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-gray-600 mb-1">טיוטת מסר ללקוח (לא נשלח):</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{f.customerReplyDraft}</p>
          </div>
        )}
      </Section>

      <Section title="שאלות פתוחות ומידע חסר" testId="briefing-open-questions">
        {b.openQuestions.length === 0 ? (
          <Unknown data-testid="briefing-no-open-questions">
            העובד לא סימן שאלות פתוחות או מידע חסר בריצה הזו.
          </Unknown>
        ) : (
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
            {b.openQuestions.map((q, i) => (
              <li key={i}>{q.text}</li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="הקשר" testId="briefing-context">
        {b.context.technician && <KV label="טכנאי אחראי" value={b.context.technician} />}
        {b.context.deviceIdentifier && <KV label="מכשיר" value={b.context.deviceIdentifier} />}
        {b.context.inputSummary && <KV label="הבקשה שנשלחה" value={b.context.inputSummary} />}
        {b.context.ticketDescription && <KV label="תיאור הקריאה" value={b.context.ticketDescription} />}
        {b.context.environment.map((row) => <KV key={row.label} label={row.label} value={row.value} />)}
        {conversation?.participants?.length > 0 && (
          <KV
            label="משתתפים בשיחה"
            value={conversation.participants
              .filter((p) => p.role === "agent")
              .map((p) => p.agent_name || "עובד דיגיטלי")
              .join(", ") || "—"}
          />
        )}
        {conversationError && (
          <Unknown data-testid="briefing-conversation-unknown">
            הקשר השיחה לא נטען — ייתכן שיש מידע נוסף בשיחה המשויכת לריצה.
          </Unknown>
        )}
        {!conversation && !conversationError && !b.evidence.conversationId && (
          <Unknown data-testid="briefing-no-conversation">הריצה אינה מקושרת לשיחת AI.</Unknown>
        )}
        {b.context.kbArticles.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">מאמרי ידע שנעשה בהם שימוש:</p>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {b.context.kbArticles.map((art, i) => (
                <li key={i}>{art.title || art.id || "מאמר ידע"}</li>
              ))}
            </ul>
          </div>
        )}
        {b.context.similarTickets.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-600 mb-1">קריאות דומות:</p>
            <ul className="text-xs text-gray-600 space-y-0.5">
              {b.context.similarTickets.map((ref, i) => (
                <li key={i}>
                  {ref.title || "קריאה דומה"}
                  {ref.resolution ? ` — פתרון: ${ref.resolution}` : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section title="ראיות ומקורות" testId="briefing-evidence">
        {b.evidence.sources.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {b.evidence.sources.map((s, i) => (
              <span key={i} className="text-xs bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">
                {EVIDENCE_SOURCE_LABEL[s] || s}
              </span>
            ))}
          </div>
        ) : (
          <Unknown>לא סומנו מקורות ראיות בניתוח.</Unknown>
        )}
        {b.evidence.execution && (
          <div className={`rounded-md border px-2.5 py-2 text-xs ${
            b.evidence.execution.ok ? "border-green-200 bg-green-50 text-green-700" : "border-amber-200 bg-amber-50 text-amber-700"
          }`}>
            {b.evidence.execution.text || (b.evidence.execution.ok ? "הפעולה בוצעה" : "הפעולה לא בוצעה בהצלחה")}
            {b.evidence.execution.type && <span className="text-gray-400"> ({b.evidence.execution.type})</span>}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {links.map((l) => (
            l.available ? (
              <Link key={l.key} to={l.to} className="text-blue-600 hover:underline">
                {l.label}
              </Link>
            ) : (
              <span key={l.key} className="text-gray-400">{l.label} — לא זמין</span>
            )
          ))}
        </div>
        <div className="text-xs text-gray-400 flex flex-wrap gap-3">
          <span>התחלה: {b.evidence.startedAt ? new Date(b.evidence.startedAt).toLocaleString("he-IL") : "לא ידוע"}</span>
          <span>סיום: {b.evidence.finishedAt ? new Date(b.evidence.finishedAt).toLocaleString("he-IL") : "טרם הסתיימה"}</span>
        </div>
      </Section>
    </div>
  );
}
